import { useState } from "react";
import { Check } from "lucide-react";
import logoIconOnly from "../../assets/logo-icon-only.webp";
import logoWithText from "../../assets/logo-with-text.webp";
import StipplMapFrame from "../../imports/Frame1115-17-157";
import SatelliteMapFrame from "../../imports/Frame1117-17-178";
import CityMapFrame from "../../imports/Frame1121-17-187";
import PolarisMapFrame from "../../imports/Frame1125-17-196";

const colorThemes = [
	{ name: "Sleep", label: "primary 1", color: "#704214" },
	{ name: "Transport", label: "primary 2", color: "#D4A574" },
	{ name: "See & Do", label: "primary 3", color: "#A67C6D" },
	{ name: "Eat & Drink", label: "primary 4", color: "#2D2D2D" },
];

export function BrandingCard() {
	const [selectedColor, setSelectedColor] = useState(0);
	const [selectedMap, setSelectedMap] = useState(0);
	const [selectedLogo, setSelectedLogo] = useState(0);

	return (
		<div
			className="w-[450px] rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:scale-105 relative bg-white"
			style={{
				border: "1px solid #e5e7eb",
			}}
		>
			{/* Number badge */}
			<div
				className="absolute top-4 left-4 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-base shadow-md z-10"
				style={{
					background: "linear-gradient(135deg, #C084FC 0%, #A78BFA 100%)",
				}}
			>
				1
			</div>

			{/* Content at top */}
			<div className="pt-16 px-6 pb-4">
				<h3 className="text-2xl font-bold mb-2 text-gray-900">Set Up Your Branding</h3>
				<p className="text-gray-500 text-sm leading-relaxed mb-6">
					Choose your brand colors and map style. All changes apply instantly across your itineraries.
				</p>

				{/* Logo Section */}
				<div className="mb-6">
					<h4 className="text-xs font-semibold text-gray-500 mb-2">Logo</h4>
					<div className="grid grid-cols-2 gap-2">
						{/* Full Logo with Text */}
						<button
							onClick={() => setSelectedLogo(0)}
							className="relative rounded-lg overflow-hidden transition-all hover:scale-105 bg-white p-4 flex items-center justify-center"
							style={{
								border: selectedLogo === 0 ? "2px solid #A78BFA" : "2px solid #e5e7eb",
								minHeight: "80px",
							}}
						>
							<img src={logoWithText} alt="Golden Leaf Logo with Text" className="h-12 object-contain" />
							{selectedLogo === 0 && (
								<div
									className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full flex items-center justify-center"
									style={{ background: "#A78BFA" }}
								>
									<Check className="w-2.5 h-2.5 text-white" />
								</div>
							)}
						</button>

						{/* Icon Only Logo */}
						<button
							onClick={() => setSelectedLogo(1)}
							className="relative rounded-lg overflow-hidden transition-all hover:scale-105 bg-white p-4 flex items-center justify-center"
							style={{
								border: selectedLogo === 1 ? "2px solid #A78BFA" : "2px solid #e5e7eb",
								minHeight: "80px",
							}}
						>
							<img src={logoIconOnly} alt="Golden Leaf Icon Only" className="h-12 object-contain" />
							{selectedLogo === 1 && (
								<div
									className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full flex items-center justify-center"
									style={{ background: "#A78BFA" }}
								>
									<Check className="w-2.5 h-2.5 text-white" />
								</div>
							)}
						</button>
					</div>
				</div>

				{/* Colors Section */}
				<div className="mb-6">
					<h4 className="text-xs font-semibold text-gray-500 mb-2">Colors</h4>
					<div className="grid grid-cols-4 gap-2">
						{colorThemes.map((theme, idx) => (
							<button key={idx} onClick={() => setSelectedColor(idx)} className="flex flex-col items-center gap-1.5 p-1.5 rounded-lg transition-all">
								<div
									className="w-11 h-11 rounded-full shadow-sm relative transition-transform hover:scale-110 cursor-pointer"
									style={{
										backgroundColor: theme.color,
										border: selectedColor === idx ? "2px solid #A78BFA" : "2px solid transparent",
									}}
								>
									{selectedColor === idx && (
										<div className="absolute inset-0 flex items-center justify-center">
											<Check className="w-4 h-4 text-white drop-shadow-lg" />
										</div>
									)}
								</div>
								<div className="text-[10px] font-medium text-gray-700">{theme.name}</div>
								<div className="text-[9px] text-gray-400">{theme.label}</div>
							</button>
						))}
					</div>
				</div>

				{/* Map Style Section */}
				<div>
					<h4 className="text-xs font-semibold text-gray-500 mb-2">Map style</h4>
					<div className="grid grid-cols-4 gap-1.5">
						{/* City Map */}
						<button
							onClick={() => setSelectedMap(0)}
							className="relative rounded-md overflow-hidden transition-all hover:scale-105 bg-white p-2 flex flex-col items-center justify-center"
							style={{
								border: selectedMap === 0 ? "2px solid #A78BFA" : "2px solid #e5e7eb",
								height: "110px",
							}}
						>
							<div className="scale-[0.7]">
								<CityMapFrame />
							</div>
							{selectedMap === 0 && (
								<div
									className="absolute top-1.5 right-1.5 w-3.5 h-3.5 rounded-full flex items-center justify-center"
									style={{ background: "#A78BFA" }}
								>
									<Check className="w-2 h-2 text-white" />
								</div>
							)}
						</button>

						{/* Stippl Map */}
						<button
							onClick={() => setSelectedMap(1)}
							className="relative rounded-md overflow-hidden transition-all hover:scale-105 bg-white p-2 flex flex-col items-center justify-center"
							style={{
								border: selectedMap === 1 ? "2px solid #A78BFA" : "2px solid #e5e7eb",
								height: "110px",
							}}
						>
							<div className="scale-[0.7]">
								<StipplMapFrame />
							</div>
							{selectedMap === 1 && (
								<div
									className="absolute top-1.5 right-1.5 w-3.5 h-3.5 rounded-full flex items-center justify-center"
									style={{ background: "#A78BFA" }}
								>
									<Check className="w-2 h-2 text-white" />
								</div>
							)}
						</button>

						{/* Satellite Map */}
						<button
							onClick={() => setSelectedMap(2)}
							className="relative rounded-md overflow-hidden transition-all hover:scale-105 bg-white p-2 flex flex-col items-center justify-center"
							style={{
								border: selectedMap === 2 ? "2px solid #A78BFA" : "2px solid #e5e7eb",
								height: "110px",
							}}
						>
							<div className="scale-[0.7]">
								<SatelliteMapFrame />
							</div>
							{selectedMap === 2 && (
								<div
									className="absolute top-1.5 right-1.5 w-3.5 h-3.5 rounded-full flex items-center justify-center"
									style={{ background: "#A78BFA" }}
								>
									<Check className="w-2 h-2 text-white" />
								</div>
							)}
						</button>

						{/* Polaris Map */}
						<button
							onClick={() => setSelectedMap(3)}
							className="relative rounded-md overflow-hidden transition-all hover:scale-105 bg-white p-2 flex flex-col items-center justify-center"
							style={{
								border: selectedMap === 3 ? "2px solid #A78BFA" : "2px solid #e5e7eb",
								height: "110px",
							}}
						>
							<div className="scale-[0.7]">
								<PolarisMapFrame />
							</div>
							{selectedMap === 3 && (
								<div
									className="absolute top-1.5 right-1.5 w-3.5 h-3.5 rounded-full flex items-center justify-center"
									style={{ background: "#A78BFA" }}
								>
									<Check className="w-2 h-2 text-white" />
								</div>
							)}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
